(() => {
  // src/core/constants.js
  var MILLIMETERS_PER_UNIT = Object.freeze({
    millimeter: 1,
    stud: 8,
    plate: 3.2,
    brick: 9.6
  });
  var SUPPORTED_UNITS = Object.freeze(Object.keys(MILLIMETERS_PER_UNIT));
  var GRID_SPACING_MM = Object.freeze({
    fullStud: 8,
    halfStud: 4,
    plate: 3.2
  });
  var GEOMETRY_TOLERANCE_MM = 1e-9;
  var FINISHING_TILE_THICKNESS_MM = MILLIMETERS_PER_UNIT.plate;

  // src/core/conversions.js
  function assertSupportedUnit(unit) {
    if (!Object.hasOwn(MILLIMETERS_PER_UNIT, unit)) {
      throw new RangeError(`Unsupported unit: ${String(unit)}`);
    }
  }
  function assertFiniteNumber(value, name = "value") {
    if (!Number.isFinite(value)) throw new TypeError(`${name} must be finite.`);
  }
  function assertDistance(value, name = "value") {
    assertFiniteNumber(value, name);
    if (value < 0) throw new RangeError(`${name} must not be negative.`);
  }
  function finiteResult(value) {
    if (!Number.isFinite(value)) throw new RangeError("Conversion result must be finite.");
    return value;
  }
  function toMillimeters(value, unit) {
    assertDistance(value);
    assertSupportedUnit(unit);
    return finiteResult(value * MILLIMETERS_PER_UNIT[unit]);
  }
  function fromMillimeters(millimeters, unit) {
    assertFiniteNumber(millimeters, "millimeters");
    assertSupportedUnit(unit);
    const value = finiteResult(millimeters / MILLIMETERS_PER_UNIT[unit]);
    const nearestInteger = Math.round(value);
    if (Number.isSafeInteger(nearestInteger) && Math.abs(nearestInteger * MILLIMETERS_PER_UNIT[unit] - millimeters) <= GEOMETRY_TOLERANCE_MM) {
      return nearestInteger;
    }
    return value;
  }
  function equivalentsFromMillimeters(millimeters) {
    return Object.freeze({
      millimeters,
      studs: fromMillimeters(millimeters, "stud"),
      plates: fromMillimeters(millimeters, "plate"),
      bricks: fromMillimeters(millimeters, "brick")
    });
  }
  function convertDistance({ value, unit }) {
    return equivalentsFromMillimeters(toMillimeters(value, unit));
  }
  function convertOffsetFromMillimeters(differenceMm) {
    assertFiniteNumber(differenceMm, "differenceMm");
    return equivalentsFromMillimeters(differenceMm);
  }

  // src/core/geometry.js
  function isWithinTolerance(actual, expected = 0) {
    assertFiniteNumber(actual, "actual");
    assertFiniteNumber(expected, "expected");
    return Math.abs(actual - expected) <= GEOMETRY_TOLERANCE_MM;
  }
  function normalizeNearZero(value) {
    return isWithinTolerance(value, 0) ? 0 : value;
  }
  function checkAlignment(distanceA, distanceB) {
    const convertedA = convertDistance(distanceA);
    const convertedB = convertDistance(distanceB);
    const rawDifferenceMm = convertedB.millimeters - convertedA.millimeters;
    if (!Number.isFinite(rawDifferenceMm)) throw new RangeError("Alignment difference must be finite.");
    const aligned = isWithinTolerance(rawDifferenceMm, 0);
    const differenceMm = aligned ? 0 : rawDifferenceMm;
    return Object.freeze({
      distanceA: convertedA,
      distanceB: convertedB,
      differenceMm,
      difference: convertOffsetFromMillimeters(differenceMm),
      aligned,
      longerDistance: aligned ? null : differenceMm > 0 ? "B" : "A"
    });
  }
  function calculateSnotBuild(distance, { addFinishingTile = false } = {}) {
    if (typeof addFinishingTile !== "boolean") {
      throw new TypeError("addFinishingTile must be a boolean.");
    }
    const entered = convertDistance(distance);
    const finishingTileMm = addFinishingTile ? FINISHING_TILE_THICKNESS_MM : 0;
    const finalMillimeters = entered.millimeters + finishingTileMm;
    if (!Number.isFinite(finalMillimeters)) throw new RangeError("Final SNOT distance must be finite.");
    return Object.freeze({
      entered,
      addFinishingTile,
      finishingTileMm,
      final: convertDistance({ value: finalMillimeters, unit: "millimeter" })
    });
  }
  function checkSnotAlignment(targetDistance, snotDistance, options2 = {}) {
    const snotBuild = calculateSnotBuild(snotDistance, options2);
    const alignment = checkAlignment(targetDistance, {
      value: snotBuild.final.millimeters,
      unit: "millimeter"
    });
    return Object.freeze({ ...alignment, snotBuild });
  }
  function gridPoint(multiple, spacingMm, suppliedDistanceMm) {
    if (!Number.isSafeInteger(multiple) || multiple < 0) {
      throw new RangeError("Grid multiple must be a non-negative safe integer.");
    }
    const distanceMm = multiple * spacingMm;
    const adjustmentMm = normalizeNearZero(distanceMm - suppliedDistanceMm);
    if (!Number.isFinite(distanceMm) || !Number.isFinite(adjustmentMm)) {
      throw new RangeError("Grid result must be finite.");
    }
    return Object.freeze({ multiple, distanceMm, adjustmentMm });
  }
  function analyzeGridPosition(distanceMm, spacingMm) {
    assertDistance(distanceMm, "distanceMm");
    assertFiniteNumber(spacingMm, "spacingMm");
    if (spacingMm <= 0) throw new RangeError("spacingMm must be greater than zero.");
    const quotient = distanceMm / spacingMm;
    if (!Number.isFinite(quotient)) throw new RangeError("Grid quotient must be finite.");
    const roundedMultiple = Math.round(quotient);
    if (!Number.isSafeInteger(roundedMultiple)) {
      throw new RangeError("Distance is too large for safe grid analysis.");
    }
    const roundedPoint = gridPoint(roundedMultiple, spacingMm, distanceMm);
    const aligned = isWithinTolerance(roundedPoint.adjustmentMm, 0);
    if (aligned) {
      const exact = Object.freeze({ ...roundedPoint, adjustmentMm: 0 });
      return Object.freeze({
        spacingMm,
        aligned: true,
        exactMultiple: roundedMultiple,
        lower: exact,
        upper: exact,
        nearest: Object.freeze({ ...exact, tie: false })
      });
    }
    const lower = gridPoint(Math.floor(quotient), spacingMm, distanceMm);
    const upper = gridPoint(Math.ceil(quotient), spacingMm, distanceMm);
    const lowerMagnitude = Math.abs(lower.adjustmentMm);
    const upperMagnitude = Math.abs(upper.adjustmentMm);
    const tie = isWithinTolerance(lowerMagnitude, upperMagnitude);
    const selected = tie || upperMagnitude < lowerMagnitude ? upper : lower;
    return Object.freeze({
      spacingMm,
      aligned: false,
      exactMultiple: null,
      lower,
      upper,
      nearest: Object.freeze({ ...selected, tie })
    });
  }
  function analyzeDistanceGrids(distance) {
    const converted = convertDistance(distance);
    const distanceMm = converted.millimeters;
    return Object.freeze({
      distanceMm,
      fullStud: analyzeGridPosition(distanceMm, GRID_SPACING_MM.fullStud),
      halfStud: analyzeGridPosition(distanceMm, GRID_SPACING_MM.halfStud),
      plate: analyzeGridPosition(distanceMm, GRID_SPACING_MM.plate)
    });
  }

  // src/core/presets.js
  var preset = (id, label, studs, plates) => Object.freeze({
    id,
    label,
    distanceA: Object.freeze({ value: studs, unit: "stud" }),
    distanceB: Object.freeze({ value: plates, unit: "plate" })
  });
  var SNOT_PRESETS = Object.freeze([
    preset("five-plates-two-studs", "5 Plates \u2194 2 Studs", 2, 5),
    preset("ten-plates-four-studs", "10 Plates \u2194 4 Studs", 4, 10),
    preset("fifteen-plates-six-studs", "15 Plates \u2194 6 Studs", 6, 15),
    preset("twenty-plates-eight-studs", "20 Plates \u2194 8 Studs", 8, 20)
  ]);
  function getPreset(id) {
    return SNOT_PRESETS.find((candidate) => candidate.id === id) ?? null;
  }

  // src/core/recommendations.js
  var BUILDING_UNITS = Object.freeze(["stud", "plate", "brick"]);
  var SUGGESTION_SIDES = Object.freeze(["target", "snot"]);
  var WHOLE_ALIGNMENT_SCALE_PER_MM = 10;
  function normalizeAdjustment(value) {
    return Math.abs(value) <= GEOMETRY_TOLERANCE_MM ? 0 : value;
  }
  function assertBuildingUnit(unit) {
    if (!BUILDING_UNITS.includes(unit)) {
      throw new RangeError(`Whole-count calculations require a building unit; received ${String(unit)}.`);
    }
  }
  function scaledMillimeters(millimeters, name) {
    const scaled = millimeters * WHOLE_ALIGNMENT_SCALE_PER_MM;
    if (!Number.isSafeInteger(scaled)) {
      throw new RangeError(`${name} must resolve to safe tenths of a millimeter.`);
    }
    return scaled;
  }
  function unitTicks(unit) {
    assertBuildingUnit(unit);
    return scaledMillimeters(MILLIMETERS_PER_UNIT[unit], `${unit} unit`);
  }
  function normalizeCountNearInteger(value, unit) {
    assertDistance(value, "count");
    assertBuildingUnit(unit);
    const nearestInteger = Math.round(value);
    if (!Number.isSafeInteger(nearestInteger)) {
      throw new RangeError("Count is too large for safe integer normalization.");
    }
    return Math.abs((nearestInteger - value) * MILLIMETERS_PER_UNIT[unit]) <= GEOMETRY_TOLERANCE_MM ? nearestInteger : value;
  }
  function wholePoint(value, unit, distanceMm) {
    if (!Number.isSafeInteger(value) || value < 0) {
      throw new RangeError("Whole-unit value must be a non-negative safe integer.");
    }
    const millimeters = value * MILLIMETERS_PER_UNIT[unit];
    const adjustmentMm = normalizeAdjustment(millimeters - distanceMm);
    if (!Number.isFinite(millimeters) || !Number.isFinite(adjustmentMm)) {
      throw new RangeError("Whole-unit recommendation must be finite.");
    }
    return Object.freeze({ value, millimeters, adjustmentMm });
  }
  function recommendWholeUnits(distanceMm, unit) {
    assertDistance(distanceMm, "distanceMm");
    assertBuildingUnit(unit);
    const exactValue = normalizeCountNearInteger(fromMillimeters(distanceMm, unit), unit);
    const roundedValue = Math.round(exactValue);
    if (!Number.isSafeInteger(roundedValue)) {
      throw new RangeError("Distance is too large for safe whole-unit analysis.");
    }
    const roundedPoint = wholePoint(roundedValue, unit, distanceMm);
    const isWhole = Number.isInteger(exactValue);
    if (isWhole) {
      const exact = Object.freeze({ ...roundedPoint, adjustmentMm: 0 });
      return Object.freeze({
        distanceMm,
        unit,
        exactValue,
        isWhole: true,
        exactWholeValue: roundedValue,
        lower: exact,
        upper: exact,
        nearest: exact,
        nearestOptions: Object.freeze([exact]),
        tie: false
      });
    }
    const lower = wholePoint(Math.floor(exactValue), unit, distanceMm);
    const upper = wholePoint(Math.ceil(exactValue), unit, distanceMm);
    const lowerMagnitude = Math.abs(lower.adjustmentMm);
    const upperMagnitude = Math.abs(upper.adjustmentMm);
    const tie = Math.abs(lowerMagnitude - upperMagnitude) <= GEOMETRY_TOLERANCE_MM;
    const nearest = tie ? null : lowerMagnitude < upperMagnitude ? lower : upper;
    return Object.freeze({
      distanceMm,
      unit,
      exactValue,
      isWhole: false,
      exactWholeValue: null,
      lower,
      upper,
      nearest,
      nearestOptions: Object.freeze(tie ? [lower, upper] : [nearest]),
      tie
    });
  }
  function greatestCommonDivisor(left, right) {
    let a = Math.abs(left);
    let b = Math.abs(right);
    while (b !== 0) [a, b] = [b, a % b];
    return a;
  }
  function createPair(targetCount, snotCount, targetTicks, snotTicks, tileTicks, referenceMm) {
    const distanceTicks = targetCount * targetTicks;
    const targetMm = distanceTicks / WHOLE_ALIGNMENT_SCALE_PER_MM;
    const snotRawMm = snotCount * snotTicks / WHOLE_ALIGNMENT_SCALE_PER_MM;
    const snotFinalMm = (snotCount * snotTicks + tileTicks) / WHOLE_ALIGNMENT_SCALE_PER_MM;
    if (targetMm !== snotFinalMm) throw new Error("Discrete whole-count pair is not aligned.");
    return Object.freeze({
      targetCount,
      snotCount,
      targetMm,
      snotRawMm,
      snotFinalMm,
      distanceMm: targetMm,
      adjustmentMm: normalizeAdjustment(targetMm - referenceMm)
    });
  }
  function enumerateWholePairs(targetTicks, snotTicks, tileTicks, maxDistanceTicks, referenceMm) {
    const divisor = greatestCommonDivisor(targetTicks, snotTicks);
    if (tileTicks % divisor !== 0) return [];
    const targetStep = snotTicks / divisor;
    const snotStep = targetTicks / divisor;
    let baseTarget = null;
    for (let targetCount = 0; targetCount < targetStep; targetCount += 1) {
      if ((targetTicks * targetCount - tileTicks) % snotTicks === 0) {
        baseTarget = targetCount;
        break;
      }
    }
    if (baseTarget === null) return [];
    let baseSnot = (targetTicks * baseTarget - tileTicks) / snotTicks;
    const firstStep = Math.max(0, Math.ceil(-baseSnot / snotStep));
    baseTarget += firstStep * targetStep;
    baseSnot += firstStep * snotStep;
    const pairs = [];
    for (let targetCount = baseTarget, snotCount = baseSnot; targetCount * targetTicks <= maxDistanceTicks; targetCount += targetStep, snotCount += snotStep) {
      pairs.push(createPair(targetCount, snotCount, targetTicks, snotTicks, tileTicks, referenceMm));
    }
    return pairs;
  }
  function dimensionalCounterpart(sourceSide, referenceMm, targetUnit, snotUnit, tileMm) {
    if (sourceSide === "target") {
      const requiredRawMm = referenceMm - tileMm;
      if (requiredRawMm < -GEOMETRY_TOLERANCE_MM) {
        return Object.freeze({ side: "snot", unit: snotUnit, possible: false, value: null });
      }
      const normalizedRawMm = Math.abs(requiredRawMm) <= GEOMETRY_TOLERANCE_MM ? 0 : requiredRawMm;
      const targetEquivalent = fromMillimeters(referenceMm, snotUnit);
      const tileEquivalent = fromMillimeters(tileMm, snotUnit);
      return Object.freeze({
        side: "snot",
        unit: snotUnit,
        possible: true,
        value: normalizeCountNearInteger(
          normalizedRawMm === 0 ? 0 : targetEquivalent - tileEquivalent,
          snotUnit
        )
      });
    }
    return Object.freeze({
      side: "target",
      unit: targetUnit,
      possible: true,
      value: normalizeCountNearInteger(fromMillimeters(referenceMm, targetUnit), targetUnit)
    });
  }
  function findWholeNumberAlignment({
    targetUnit,
    snotUnit,
    addFinishingTile = false,
    referenceSide,
    referenceValue,
    maxDistanceMm
  }) {
    assertBuildingUnit(targetUnit);
    assertBuildingUnit(snotUnit);
    if (!SUGGESTION_SIDES.includes(referenceSide)) {
      throw new RangeError(`Unsupported recommendation reference: ${String(referenceSide)}`);
    }
    if (typeof addFinishingTile !== "boolean") {
      throw new TypeError("addFinishingTile must be a boolean.");
    }
    assertDistance(referenceValue, "referenceValue");
    assertDistance(maxDistanceMm, "maxDistanceMm");
    const targetTicks = unitTicks(targetUnit);
    const snotTicks = unitTicks(snotUnit);
    const tileMm = addFinishingTile ? FINISHING_TILE_THICKNESS_MM : 0;
    const tileTicks = scaledMillimeters(tileMm, "finishing Tile");
    const maxDistanceTicks = Math.floor((maxDistanceMm + GEOMETRY_TOLERANCE_MM) * WHOLE_ALIGNMENT_SCALE_PER_MM);
    const rawReferenceMm = convertDistance({
      value: referenceValue,
      unit: referenceSide === "target" ? targetUnit : snotUnit
    }).millimeters;
    const referenceMm = rawReferenceMm + (referenceSide === "snot" ? tileMm : 0);
    const counterpart = dimensionalCounterpart(referenceSide, referenceMm, targetUnit, snotUnit, tileMm);
    const normalizedReferenceCount = normalizeCountNearInteger(
      referenceValue,
      referenceSide === "target" ? targetUnit : snotUnit
    );
    let pairs = enumerateWholePairs(targetTicks, snotTicks, tileTicks, maxDistanceTicks, referenceMm);
    if (referenceMm > GEOMETRY_TOLERANCE_MM) {
      const positivePairs = pairs.filter((pair) => pair.targetCount > 0 || pair.snotCount > 0);
      if (positivePairs.length > 0) pairs = positivePairs;
    }
    const ranked = pairs.slice().sort((left, right) => {
      const difference = Math.abs(left.adjustmentMm) - Math.abs(right.adjustmentMm);
      return Math.abs(difference) <= GEOMETRY_TOLERANCE_MM ? left.distanceMm - right.distanceMm : difference;
    });
    const nearestMagnitude = ranked.length > 0 ? Math.abs(ranked[0].adjustmentMm) : null;
    const nearestPairs = nearestMagnitude === null ? [] : ranked.filter((pair) => Math.abs(Math.abs(pair.adjustmentMm) - nearestMagnitude) <= GEOMETRY_TOLERANCE_MM);
    const tie = nearestPairs.length > 1;
    const exactForCurrentReference = Number.isInteger(normalizedReferenceCount) && nearestPairs.some((pair) => pair.adjustmentMm === 0 && (referenceSide === "target" ? pair.targetCount === normalizedReferenceCount : pair.snotCount === normalizedReferenceCount));
    return Object.freeze({
      found: nearestPairs.length > 0,
      targetUnit,
      snotUnit,
      addFinishingTile,
      finishingTileMm: tileMm,
      referenceSide,
      referenceValue: normalizedReferenceCount,
      referenceMillimeters: referenceMm,
      dimensionalCounterpart: counterpart,
      exactForCurrentReference,
      tie,
      recommendedPair: tie ? null : nearestPairs[0] ?? null,
      recommendedPairs: Object.freeze(nearestPairs),
      alternatives: Object.freeze(ranked.slice(nearestPairs.length, nearestPairs.length + 2)),
      maxDistanceMm
    });
  }

  // src/presentation/diagram.js
  var VIEWBOX = Object.freeze({ width: 720, height: 240 });
  var ORIGIN = Object.freeze({ x: 64, y: 130 });
  var DRAWING_RIGHT = 672;
  var MAX_GRID_TICKS = 32;
  var MIN_VISIBLE_OFFSET_SVG = 18;
  function annotationPlacement(x) {
    if (x < 180) return Object.freeze({ x: Math.max(24, x), anchor: "start" });
    if (x > 540) return Object.freeze({ x: Math.min(VIEWBOX.width - 24, x), anchor: "end" });
    return Object.freeze({ x, anchor: "middle" });
  }
  function niceStudStep(minimumStep) {
    if (minimumStep <= 1) return 1;
    const power = 10 ** Math.floor(Math.log10(minimumStep));
    for (const multiplier of [1, 2, 5, 10]) {
      const candidate = multiplier * power;
      if (candidate >= minimumStep) return candidate;
    }
    return 10 * power;
  }
  function createAlignmentDiagramModel({
    distanceAMm,
    distanceBMm,
    differenceMm,
    aligned,
    finishingTileMm = 0
  }) {
    for (const [name, value] of Object.entries({ distanceAMm, distanceBMm, differenceMm, finishingTileMm })) {
      if (!Number.isFinite(value)) throw new TypeError(`${name} must be finite.`);
    }
    if (distanceAMm < 0 || distanceBMm < 0) throw new RangeError("Diagram distances must not be negative.");
    if (finishingTileMm < 0 || finishingTileMm > distanceBMm) {
      throw new RangeError("Diagram finishing Tile contribution must fit inside Distance B.");
    }
    const referenceMaximumMm = Math.max(distanceAMm, distanceBMm, 8);
    const scale = (DRAWING_RIGHT - ORIGIN.x) / referenceMaximumMm;
    const endpointA = Object.freeze({ x: ORIGIN.x + distanceAMm * scale, y: ORIGIN.y });
    const endpointB = Object.freeze({ x: ORIGIN.x + distanceBMm * scale, y: ORIGIN.y });
    const totalStuds = Math.floor(referenceMaximumMm / 8);
    const studStep = niceStudStep(Math.ceil(totalStuds / MAX_GRID_TICKS));
    const gridTicks = [];
    for (let stud = 0; stud <= totalStuds; stud += studStep) {
      gridTicks.push(Object.freeze({ stud, millimeters: stud * 8, x: ORIGIN.x + stud * 8 * scale }));
    }
    const offsetSpanSvg = Math.abs(endpointB.x - endpointA.x);
    const offsetMarker = aligned ? null : Object.freeze({
      x1: endpointA.x,
      x2: endpointB.x,
      y: 82,
      label: annotationPlacement((endpointA.x + endpointB.x) / 2),
      magnifiedForVisibility: offsetSpanSvg < MIN_VISIBLE_OFFSET_SVG
    });
    const finishingTile = finishingTileMm > 0 ? Object.freeze({
      millimeters: finishingTileMm,
      x1: ORIGIN.x + (distanceBMm - finishingTileMm) * scale,
      x2: endpointB.x,
      y: ORIGIN.y + 18,
      label: annotationPlacement(ORIGIN.x + (distanceBMm - finishingTileMm / 2) * scale)
    }) : null;
    return Object.freeze({
      viewBox: VIEWBOX,
      origin: ORIGIN,
      axis: Object.freeze({ x1: ORIGIN.x, x2: DRAWING_RIGHT, y: ORIGIN.y }),
      distanceA: Object.freeze({ millimeters: distanceAMm, endpoint: endpointA }),
      distanceB: Object.freeze({ millimeters: distanceBMm, endpoint: endpointB }),
      differenceMm,
      aligned,
      scale,
      gridTicks: Object.freeze(gridTicks),
      alignedAnnotation: aligned ? annotationPlacement(endpointA.x) : null,
      offsetMarker,
      finishingTile
    });
  }

  // src/presentation/formatting.js
  var UNIT_LABELS = Object.freeze({
    millimeter: ["millimeter", "millimeters"],
    stud: ["stud", "studs"],
    plate: ["plate", "plates"],
    brick: ["brick", "bricks"]
  });
  function formatWithPrecision(value, maximumFractionDigits) {
    return new Intl.NumberFormat("en-US", {
      maximumFractionDigits,
      useGrouping: false
    }).format(value);
  }
  function formatNumber(value, maximumFractionDigits = 4, zeroTolerance = 0) {
    if (!Number.isFinite(value)) throw new TypeError("Display value must be finite.");
    if (!Number.isFinite(zeroTolerance) || zeroTolerance < 0) {
      throw new RangeError("Display zero tolerance must be a non-negative finite number.");
    }
    const normalized = Math.abs(value) <= zeroTolerance || Object.is(value, -0) ? 0 : value;
    let precision = maximumFractionDigits;
    let formatted = formatWithPrecision(normalized, precision);
    if (normalized !== 0 && Number(formatted) === 0) {
      precision = Math.min(20, Math.max(precision + 1, Math.ceil(-Math.log10(Math.abs(normalized))) + 3));
      formatted = formatWithPrecision(normalized, precision);
    }
    return formatted;
  }
  function isDisplayApproximation(value, maximumFractionDigits = 4, zeroTolerance = 0) {
    const displayed = Number(formatNumber(value, maximumFractionDigits, zeroTolerance));
    return Math.abs(displayed - value) > GEOMETRY_TOLERANCE_MM;
  }
  function unitLabel(unit, value) {
    const labels = UNIT_LABELS[unit];
    if (!labels) throw new RangeError(`Unsupported display unit: ${String(unit)}`);
    return Math.abs(Math.abs(value) - 1) <= GEOMETRY_TOLERANCE_MM ? labels[0] : labels[1];
  }
  function formatMeasurement(value, unit, { signed = false, approximate = true } = {}) {
    const millimetersPerUnit = MILLIMETERS_PER_UNIT[unit];
    if (!millimetersPerUnit) throw new RangeError(`Unsupported display unit: ${String(unit)}`);
    const zeroTolerance = GEOMETRY_TOLERANCE_MM / millimetersPerUnit;
    const number = formatNumber(value, 4, zeroTolerance);
    const sign = signed && value > zeroTolerance ? "+" : "";
    const prefix = approximate && isDisplayApproximation(value, 4, zeroTolerance) ? "\u2248 " : "";
    return `${prefix}${sign}${number} ${unitLabel(unit, value)}`;
  }
  function formatMillimeters(value, options2 = {}) {
    const number = formatNumber(value, 4, GEOMETRY_TOLERANCE_MM);
    const sign = options2.signed && value > GEOMETRY_TOLERANCE_MM ? "+" : "";
    const prefix = options2.approximate !== false && isDisplayApproximation(value, 4, GEOMETRY_TOLERANCE_MM) ? "\u2248 " : "";
    return `${prefix}${sign}${number} mm`;
  }

  // src/core/validation.js
  var STRICT_DECIMAL_PATTERN = /^(?:\d+(?:[.,]\d+)?|[.,]\d+)$/;
  var NON_FINITE_PATTERN = /^(?:nan|[+-]?infinity)$/i;
  function invalid(raw, code) {
    return Object.freeze({ valid: false, raw, code });
  }
  function parseDistanceValue(rawInput) {
    const raw = String(rawInput ?? "");
    const trimmed = raw.trim();
    if (trimmed === "") return invalid(raw, "empty");
    if (NON_FINITE_PATTERN.test(trimmed)) return invalid(raw, "non_finite");
    if (trimmed.includes(".") && trimmed.includes(",")) return invalid(raw, "mixed_separators");
    if (trimmed.startsWith("-")) return invalid(raw, "negative");
    if (!STRICT_DECIMAL_PATTERN.test(trimmed)) return invalid(raw, "malformed");
    const normalized = trimmed.replace(",", ".");
    const value = Number(normalized);
    if (!Number.isFinite(value)) return invalid(raw, "non_finite");
    if (value < 0) return invalid(raw, "negative");
    return Object.freeze({ valid: true, raw, normalized, value });
  }

  // src/ui/state.js
  var MAX_UI_DISTANCE_MM = 1e4;
  var field = (rawValue, unit) => Object.freeze({ rawValue, unit });
  function createDefaultState() {
    return Object.freeze({
      quick: field("0.5", "stud"),
      distanceA: field("2", "stud"),
      distanceB: field("5", "plate"),
      finishingTile: false,
      suggestionSource: "target",
      presetId: "five-plates-two-studs"
    });
  }
  function validateUiDistance(rawValue, unit) {
    const parsed = parseDistanceValue(rawValue);
    if (!parsed.valid) return parsed;
    try {
      assertSupportedUnit(unit);
    } catch {
      return Object.freeze({ valid: false, raw: parsed.raw, code: "unknown_unit" });
    }
    try {
      const millimeters = toMillimeters(parsed.value, unit);
      if (millimeters > MAX_UI_DISTANCE_MM) {
        return Object.freeze({ valid: false, raw: parsed.raw, code: "too_large" });
      }
      return Object.freeze({ ...parsed, unit, millimeters });
    } catch {
      return Object.freeze({ valid: false, raw: parsed.raw, code: "overflow" });
    }
  }
  function swapAlignmentState(state) {
    return Object.freeze({
      ...state,
      distanceA: field(state.distanceB.rawValue, state.distanceB.unit),
      distanceB: field(state.distanceA.rawValue, state.distanceA.unit),
      suggestionSource: "target",
      presetId: ""
    });
  }
  function setFinishingTileState(state, enabled) {
    return Object.freeze({
      ...state,
      finishingTile: Boolean(enabled),
      presetId: ""
    });
  }
  function applyPresetState(state, presetId) {
    const preset2 = getPreset(presetId);
    if (!preset2) return state;
    return Object.freeze({
      ...state,
      distanceA: field(String(preset2.distanceA.value), preset2.distanceA.unit),
      distanceB: field(String(preset2.distanceB.value), preset2.distanceB.unit),
      finishingTile: false,
      suggestionSource: "target",
      presetId
    });
  }
  function updateFieldState(state, fieldName, rawValue, unit) {
    if (!["quick", "distanceA", "distanceB"].includes(fieldName)) return state;
    return Object.freeze({
      ...state,
      [fieldName]: field(String(rawValue), unit),
      suggestionSource: fieldName === "distanceA" ? "target" : fieldName === "distanceB" ? "snot" : state.suggestionSource,
      presetId: fieldName === "quick" ? state.presetId : ""
    });
  }
  function applyWholeAlignmentState(state, pair) {
    if (!pair || !Number.isSafeInteger(pair.targetCount) || !Number.isSafeInteger(pair.snotCount) || pair.targetCount < 0 || pair.snotCount < 0) return state;
    return Object.freeze({
      ...state,
      distanceA: field(String(pair.targetCount), state.distanceA.unit),
      distanceB: field(String(pair.snotCount), state.distanceB.unit),
      presetId: ""
    });
  }

  // src/ui/svg-renderer.js
  function attribute(value) {
    return String(value).replace(/[&<>"']/g, (character) => ({
      "&": "&amp;",
      "<": "&lt;",
      ">": "&gt;",
      '"': "&quot;",
      "'": "&#39;"
    })[character]);
  }
  function renderAlignmentSvg(model, instanceId) {
    const titleId = `${instanceId}-diagram-title`;
    const descriptionId = `${instanceId}-diagram-description`;
    const description = model.aligned ? `Target Distance and SNOT Build both end at ${formatMillimeters(model.distanceA.millimeters)}.` : `Target Distance ends at ${formatMillimeters(model.distanceA.millimeters)} and SNOT Build ends at ${formatMillimeters(model.distanceB.millimeters)}. The signed offset is ${formatMillimeters(model.differenceMm, { signed: true })}.`;
    const tileDescription = model.finishingTile ? ` The SNOT Build total includes one finishing Tile adding ${formatMillimeters(model.finishingTile.millimeters)}.` : "";
    const ticks = model.gridTicks.map((tick) => `
    <g class="htbi-snot-svg-grid-tick" aria-hidden="true">
      <line x1="${tick.x}" y1="98" x2="${tick.x}" y2="162"></line>
      <text x="${tick.x}" y="184">${formatNumber(tick.stud)}</text>
    </g>`).join("");
    const magnifiedOffset = model.offsetMarker?.magnifiedForVisibility ? `
    <g class="htbi-snot-svg-magnified-offset" aria-hidden="true">
      <rect x="140" y="8" width="440" height="58" rx="7"></rect>
      <text x="360" y="29">Visibility aid: offset magnified</text>
      <text x="360" y="53">${attribute(formatMillimeters(model.differenceMm, { signed: true }))} (B \u2212 A)</text>
    </g>` : "";
    const offset = model.offsetMarker ? `
    <g class="htbi-snot-svg-offset" aria-hidden="true">
      <line x1="${model.offsetMarker.x1}" y1="${model.offsetMarker.y}" x2="${model.offsetMarker.x2}" y2="${model.offsetMarker.y}"></line>
      <line x1="${model.offsetMarker.x1}" y1="${model.offsetMarker.y - 7}" x2="${model.offsetMarker.x1}" y2="${model.offsetMarker.y + 7}"></line>
      <line x1="${model.offsetMarker.x2}" y1="${model.offsetMarker.y - 7}" x2="${model.offsetMarker.x2}" y2="${model.offsetMarker.y + 7}"></line>
      ${model.offsetMarker.magnifiedForVisibility ? "" : `<text class="htbi-snot-svg-anchor-${model.offsetMarker.label.anchor}" x="${model.offsetMarker.label.x}" y="65">${attribute(formatMillimeters(model.differenceMm, { signed: true }))}</text>`}
    </g>
    ${magnifiedOffset}` : `
    <text class="htbi-snot-svg-aligned-label htbi-snot-svg-anchor-${model.alignedAnnotation.anchor}" x="${model.alignedAnnotation.x}" y="78" aria-hidden="true">Endpoints align</text>`;
    const finishingTile = model.finishingTile ? `
    <g class="htbi-snot-svg-finishing-tile" aria-hidden="true">
      <line x1="${model.finishingTile.x1}" y1="${model.finishingTile.y}" x2="${model.finishingTile.x2}" y2="${model.finishingTile.y}"></line>
      <text class="htbi-snot-svg-anchor-${model.finishingTile.label.anchor}" x="${model.finishingTile.label.x}" y="112">Finishing Tile +${attribute(formatMillimeters(model.finishingTile.millimeters))}</text>
    </g>` : "";
    return `<svg class="htbi-snot-svg" role="img" aria-labelledby="${titleId} ${descriptionId}" viewBox="0 0 ${model.viewBox.width} ${model.viewBox.height}" preserveAspectRatio="xMidYMid meet">
    <title id="${titleId}">Visual alignment of Target Distance and SNOT Build</title>
    <desc id="${descriptionId}">${attribute(description + tileDescription)}</desc>
    <line class="htbi-snot-svg-axis" x1="${model.axis.x1}" y1="${model.axis.y}" x2="${model.axis.x2}" y2="${model.axis.y}"></line>
    ${ticks}
    <text class="htbi-snot-svg-grid-label" x="${model.origin.x}" y="207">Full-stud grid reference</text>
    <line class="htbi-snot-svg-distance-a" x1="${model.origin.x}" y1="${model.origin.y}" x2="${model.distanceA.endpoint.x}" y2="${model.distanceA.endpoint.y}"></line>
    <line class="htbi-snot-svg-distance-b" x1="${model.origin.x}" y1="${model.origin.y}" x2="${model.distanceB.endpoint.x}" y2="${model.distanceB.endpoint.y}"></line>
    <circle class="htbi-snot-svg-origin" cx="${model.origin.x}" cy="${model.origin.y}" r="7"></circle>
    <circle class="htbi-snot-svg-endpoint-a" cx="${model.distanceA.endpoint.x}" cy="${model.distanceA.endpoint.y}" r="8"></circle>
    <rect class="htbi-snot-svg-endpoint-b" x="${model.distanceB.endpoint.x - 6}" y="${model.distanceB.endpoint.y - 6}" width="12" height="12" transform="rotate(45 ${model.distanceB.endpoint.x} ${model.distanceB.endpoint.y})"></rect>
    ${finishingTile}
    ${model.aligned ? `<circle class="htbi-snot-svg-endpoint-aligned" cx="${model.distanceA.endpoint.x}" cy="${model.distanceA.endpoint.y}" r="13"></circle>` : ""}
    ${offset}
    <g class="htbi-snot-svg-legend" aria-hidden="true">
      <line class="htbi-snot-svg-distance-a" x1="390" y1="220" x2="422" y2="220"></line><text x="430" y="225">Target</text>
      <line class="htbi-snot-svg-distance-b" x1="520" y1="220" x2="552" y2="220"></line><text x="560" y="225">SNOT Build</text>
    </g>
  </svg>`;
  }

  // src/ui/calculator.js
  var UNIT_OPTIONS = Object.freeze([
    ["stud", "Studs"],
    ["plate", "Plates"],
    ["brick", "Bricks"],
    ["millimeter", "Millimeters"]
  ]);
  var ERROR_MESSAGES = Object.freeze({
    empty: "Enter a distance.",
    negative: "Distance cannot be negative.",
    non_finite: "Enter a finite number.",
    mixed_separators: "Use either a decimal point or comma, not both.",
    malformed: "Enter a decimal number without letters or symbols.",
    unknown_unit: "Choose a supported unit.",
    overflow: "That value is too large to calculate.",
    too_large: `Enter a distance no greater than ${MAX_UI_DISTANCE_MM.toLocaleString("en-US")} mm or its equivalent.`
  });
  function options(selected) {
    return UNIT_OPTIONS.map(([value, label]) => `<option value="${value}"${selected === value ? " selected" : ""}>${label}</option>`).join("");
  }
  function fieldMarkup(instanceId, key, legend, value, unit, supplement = "") {
    const inputId = `${instanceId}-${key}-value`;
    const unitId = `${instanceId}-${key}-unit`;
    const errorId = `${instanceId}-${key}-error`;
    return `<fieldset class="htbi-snot-distance-card">
    <legend>${legend}</legend>
    <div class="htbi-snot-field-row">
      <div class="htbi-snot-field">
        <label for="${inputId}">Value</label>
        <input id="${inputId}" data-distance-value="${key}" inputmode="decimal" autocomplete="off" value="${value}" aria-describedby="${errorId}">
      </div>
      <div class="htbi-snot-field">
        <label for="${unitId}">Unit</label>
        <select id="${unitId}" data-distance-unit="${key}" aria-describedby="${errorId}">${options(unit)}</select>
      </div>
    </div>
    ${supplement}
    <p class="htbi-snot-error" id="${errorId}" data-error="${key}"></p>
  </fieldset>`;
  }
  function finishingTileMarkup(instanceId, checked) {
    const checkboxId = `${instanceId}-finishing-tile`;
    return `<div class="htbi-snot-tile-option">
    <input id="${checkboxId}" data-finishing-tile type="checkbox"${checked ? " checked" : ""}>
    <label for="${checkboxId}">Add finishing Tile (+${formatMillimeters(FINISHING_TILE_THICKNESS_MM)})</label>
  </div>`;
  }
  function calculatorMarkup(instanceId) {
    const initial = createDefaultState();
    const presetOptions = SNOT_PRESETS.map((preset2) => `<option value="${preset2.id}"${preset2.id === initial.presetId ? " selected" : ""}>${preset2.label}</option>`).join("");
    return `<div class="htbi-snot-calculator">
    <header class="htbi-snot-header">
      <p class="htbi-snot-eyebrow">HTBI Builder Tool</p>
      <h1>SNOT &amp; Offset Calculator</h1>
      <p>Compare building dimensions and check whether different building directions align on the same physical grid.</p>
    </header>

    <section class="htbi-snot-panel" aria-labelledby="${instanceId}-quick-heading">
      <div class="htbi-snot-section-heading">
        <p class="htbi-snot-step">1</p>
        <div><h2 id="${instanceId}-quick-heading">Quick Offset Converter</h2><p>Convert one distance and check its building-grid alignment.</p></div>
      </div>
      <div class="htbi-snot-section-explanation">
        <p><strong>What is this for?</strong> Enter a distance from your build to see its equivalent in studs, plates, bricks, and millimeters, and whether it lands on a standard building grid.</p>
        <p><strong>Example:</strong> 0.5 stud = 4 mm, which lands exactly on the half-stud grid.</p>
      </div>
      ${fieldMarkup(instanceId, "quick", "Distance to convert", initial.quick.rawValue, initial.quick.unit)}
      <div data-quick-results></div>
      <p class="htbi-snot-sr-only" data-quick-status role="status" aria-live="polite" aria-atomic="true"></p>
    </section>

    <section class="htbi-snot-panel" aria-labelledby="${instanceId}-alignment-heading">
      <div class="htbi-snot-section-heading">
        <p class="htbi-snot-step">2</p>
        <div><h2 id="${instanceId}-alignment-heading">SNOT Alignment Calculator</h2><p>Compare a target with a side-built dimension.</p></div>
      </div>
      <div class="htbi-snot-section-explanation">
        <p><strong>What is this for?</strong> Compare a target distance with a SNOT or side-built distance to see whether both surfaces end at the same physical position.</p>
        <p><strong>Example:</strong> 2 studs = 16 mm and 5 plate-heights = 16 mm, so they align perfectly.</p>
        <p>If your SNOT build ends with a Tile, enable <strong>Add finishing Tile</strong> and the calculator will add one Tile thickness (${formatMillimeters(FINISHING_TILE_THICKNESS_MM)}) to the final SNOT distance.</p>
      </div>
      <div class="htbi-snot-preset-row">
        <div class="htbi-snot-field htbi-snot-preset-field">
          <label for="${instanceId}-preset">Common SNOT preset</label>
          <select id="${instanceId}-preset" data-preset>${presetOptions}</select>
        </div>
        <button type="button" class="htbi-snot-secondary-button" data-swap>Swap distances</button>
        <button type="button" class="htbi-snot-secondary-button" data-reset>Reset</button>
      </div>
      <div class="htbi-snot-comparison-grid">
        ${fieldMarkup(instanceId, "distanceA", "Target Distance", initial.distanceA.rawValue, initial.distanceA.unit)}
        ${fieldMarkup(instanceId, "distanceB", "SNOT Build", initial.distanceB.rawValue, initial.distanceB.unit, finishingTileMarkup(instanceId, initial.finishingTile))}
      </div>
      <div data-counterpart-suggestion></div>
      <div class="htbi-snot-alignment-results" data-alignment-results></div>
      <p class="htbi-snot-sr-only" data-alignment-status role="status" aria-live="polite" aria-atomic="true"></p>
    </section>

    <section class="htbi-snot-panel" aria-labelledby="${instanceId}-visual-heading">
      <div class="htbi-snot-section-heading">
        <p class="htbi-snot-step">3</p>
        <div><h2 id="${instanceId}-visual-heading">Visual Alignment Explorer</h2><p>See where both dimensions end on the same scale.</p></div>
      </div>
      <div class="htbi-snot-section-explanation">
        <p><strong>How to read this diagram:</strong> The two lines show the target distance and the SNOT build distance. If their endpoints meet, the dimensions align. If they end at different positions, the gap is the remaining offset.</p>
      </div>
      <div class="htbi-snot-visual" data-visual></div>
      <p class="htbi-snot-note">This diagram explains nominal geometry only. It does not validate real parts or building techniques.</p>
    </section>
  </div>`;
  }
  function setValidation(root, key, result) {
    const input = root.querySelector(`[data-distance-value="${key}"]`);
    const unit = root.querySelector(`[data-distance-unit="${key}"]`);
    const error = root.querySelector(`[data-error="${key}"]`);
    const invalid2 = !result.valid;
    input.setAttribute("aria-invalid", String(invalid2));
    unit.setAttribute("aria-invalid", String(invalid2 && result.code === "unknown_unit"));
    error.textContent = invalid2 ? ERROR_MESSAGES[result.code] ?? "Unable to calculate this distance." : "";
  }
  function adjustmentWords(adjustmentMm, qualifier = "than this distance") {
    if (adjustmentMm === 0) return "No adjustment";
    if (qualifier === "too") {
      return `${formatMillimeters(Math.abs(adjustmentMm))} too ${adjustmentMm < 0 ? "short" : "long"}`;
    }
    return `${formatMillimeters(Math.abs(adjustmentMm))} ${adjustmentMm < 0 ? "shorter" : "longer"}${qualifier ? ` ${qualifier}` : ""}`;
  }
  function wholeRecommendationMarkup(recommendation, { compact = false, qualifier } = {}) {
    if (recommendation.isWhole) return "";
    const points = [recommendation.lower, recommendation.upper];
    const rows = points.map((point) => {
      const isNearest = recommendation.tie || point === recommendation.nearest;
      const label = recommendation.tie ? "Equally close whole count" : isNearest ? "Nearest whole count" : "Adjacent whole count";
      return `<div class="htbi-snot-whole-option${isNearest ? " htbi-snot-whole-nearest" : ""}">
      <p class="htbi-snot-whole-label">${label}</p>
      <p><strong>${formatMeasurement(point.value, recommendation.unit, { approximate: false })}</strong> = ${formatMillimeters(point.millimeters)}</p>
      <p>${adjustmentWords(point.adjustmentMm, qualifier)}</p>
    </div>`;
    }).join("");
    return `<div class="htbi-snot-whole-guidance${compact ? " htbi-snot-whole-guidance-compact" : ""}">
    ${recommendation.tie ? '<p class="htbi-snot-whole-tie"><strong>Equally close whole counts</strong></p>' : ""}
    <div class="htbi-snot-whole-options">${rows}</div>
  </div>`;
  }
  function equivalentCard(label, measurement, recommendation = null) {
    return `<div><dt>${label}</dt><dd><span class="htbi-snot-equivalent-value">${measurement}</span>${recommendation && !recommendation.isWhole ? wholeRecommendationMarkup(recommendation, { compact: true, qualifier: "than this distance" }) : ""}</dd></div>`;
  }
  function equivalentsMarkup(converted) {
    return `<dl class="htbi-snot-equivalents">
    ${equivalentCard("Millimeters", formatMillimeters(converted.millimeters))}
    ${equivalentCard("Studs", formatMeasurement(converted.studs, "stud"), recommendWholeUnits(converted.millimeters, "stud"))}
    ${equivalentCard("Plates", formatMeasurement(converted.plates, "plate"), recommendWholeUnits(converted.millimeters, "plate"))}
    ${equivalentCard("Bricks", formatMeasurement(converted.bricks, "brick"), recommendWholeUnits(converted.millimeters, "brick"))}
  </dl>`;
  }
  function gridCount(value, singular, plural) {
    const formatted = formatNumber(value);
    return `${formatted} ${formatted === "1" ? singular : plural}`;
  }
  function gridMarkup(title, grid, singular, plural) {
    if (grid.aligned) {
      return `<article class="htbi-snot-grid-card htbi-snot-grid-aligned"><h3>${title}</h3><p><strong>Aligned</strong></p><p>${gridCount(grid.exactMultiple, singular, plural)}</p></article>`;
    }
    return `<article class="htbi-snot-grid-card"><h3>${title}</h3><p><strong>Not aligned</strong></p>
    <p>Nearest: ${formatMillimeters(grid.nearest.distanceMm)} (${gridCount(grid.nearest.multiple, singular, plural)})</p>
    <p>Adjustment: ${formatMillimeters(grid.nearest.adjustmentMm, { signed: true })}${grid.nearest.tie ? " (tie; higher selected)" : ""}</p>
    <p>Lower: ${formatMillimeters(grid.lower.distanceMm)} (${gridCount(grid.lower.multiple, singular, plural)}), ${formatMillimeters(grid.lower.adjustmentMm, { signed: true })}</p></article>`;
  }
  function renderQuick(root, validated, announce) {
    const target = root.querySelector("[data-quick-results]");
    const status = root.querySelector("[data-quick-status]");
    if (!validated.valid) {
      target.innerHTML = '<p class="htbi-snot-unavailable">Correct the distance to see conversions and grid compatibility.</p>';
      if (announce) status.textContent = `Quick converter: ${ERROR_MESSAGES[validated.code]}`;
      return;
    }
    const distance = { value: validated.value, unit: validated.unit };
    const converted = convertDistance(distance);
    const grids = analyzeDistanceGrids(distance);
    target.innerHTML = `<h3 class="htbi-snot-result-heading">Equivalent distance</h3>${equivalentsMarkup(converted)}
    <h3 class="htbi-snot-result-heading">Grid compatibility</h3>
    <div class="htbi-snot-grid-results">
      ${gridMarkup("Full Stud Grid", grids.fullStud, "stud", "studs")}
      ${gridMarkup("Half-Stud Grid", grids.halfStud, "half-stud", "half-studs")}
      ${gridMarkup("Plate Grid", grids.plate, "plate", "plates")}
    </div>`;
    if (announce) status.textContent = `Converted: ${formatMeasurement(converted.millimeters, "millimeter", { approximate: false })}.`;
  }
  function buildablePairMarkup(result, pair, index) {
    const tileSuffix = result.addFinishingTile ? " + 1 finishing Tile" : "";
    const referenceDifference = pair.adjustmentMm === 0 ? "" : `<p>${formatMillimeters(Math.abs(pair.adjustmentMm))} ${pair.adjustmentMm < 0 ? "below" : "above"} the current ${result.referenceSide === "target" ? "Target" : "final SNOT"} distance.</p>`;
    return `<div class="htbi-snot-buildable-option">
    <p class="htbi-snot-buildable-pair"><strong>${formatMeasurement(pair.targetCount, result.targetUnit, { approximate: false })} \u2194 ${formatMeasurement(pair.snotCount, result.snotUnit, { approximate: false })}${tileSuffix}</strong></p>
    <p>Both sides use whole counts and end at ${formatMillimeters(pair.distanceMm)}.</p>
    ${referenceDifference}
    <button type="button" class="htbi-snot-secondary-button htbi-snot-use-suggestion" data-use-alignment="${index}">Use this alignment</button>
  </div>`;
  }
  function wholeAlignmentMarkup(result) {
    if (!result) {
      return `<aside class="htbi-snot-suggestion htbi-snot-suggestion-unavailable">
      <h3>Buildable whole-number match</h3>
      <p>Recommendation unavailable until the reference input is valid and both sides use Studs, Plates, or Bricks.</p>
    </aside>`;
    }
    if (!result.found) {
      return `<aside class="htbi-snot-suggestion htbi-snot-suggestion-unavailable">
      <h3>Buildable whole-number match</h3>
      <p>No whole-count alignment exists within ${formatMillimeters(result.maxDistanceMm)}.</p>
    </aside>`;
    }
    const dimensional = result.dimensionalCounterpart.possible ? `<p class="htbi-snot-dimensional-result"><span>Current dimensional equivalent:</span> <strong>${formatMeasurement(result.dimensionalCounterpart.value, result.dimensionalCounterpart.unit)}${result.dimensionalCounterpart.side === "snot" && result.addFinishingTile ? " + 1 finishing Tile" : ""}</strong></p>` : `<p class="htbi-snot-dimensional-result">No non-negative dimensional SNOT equivalent exists because the finishing Tile alone contributes ${formatMillimeters(result.finishingTileMm)}.</p>`;
    const heading = result.exactForCurrentReference ? "Exact buildable alignment" : result.tie ? "Equally near buildable alignments" : "Nearest buildable alignment";
    const pairs = result.recommendedPairs.map((pair, index) => buildablePairMarkup(result, pair, index)).join("");
    return `<aside class="htbi-snot-suggestion">
    <h3>${heading}</h3>
    ${dimensional}
    <div class="htbi-snot-buildable-options">${pairs}</div>
    <p class="htbi-snot-geometry-note">Nominal dimensional equality does not verify parts, connections, or a legal assembly.</p>
  </aside>`;
  }
  function renderWholeAlignment(root, state, validatedA, validatedB) {
    const source = state.suggestionSource === "snot" ? validatedB : validatedA;
    let result = null;
    if (source.valid && BUILDING_UNITS.includes(state.distanceA.unit) && BUILDING_UNITS.includes(state.distanceB.unit)) {
      try {
        result = findWholeNumberAlignment({
          targetUnit: state.distanceA.unit,
          snotUnit: state.distanceB.unit,
          referenceSide: state.suggestionSource,
          referenceValue: source.value,
          addFinishingTile: state.finishingTile,
          maxDistanceMm: MAX_UI_DISTANCE_MM
        });
      } catch {
        result = null;
      }
    }
    root.querySelector("[data-counterpart-suggestion]").innerHTML = wholeAlignmentMarkup(result);
    return result;
  }
  function plateHeightCount(value) {
    const formatted = formatNumber(value);
    return `${formatted} ${formatted === "1" ? "plate-height" : "plate-heights"}`;
  }
  function alignmentResultMarkup(result, snotInput) {
    const statusClass = result.aligned ? "htbi-snot-status-aligned" : "htbi-snot-status-offset";
    const explanation = result.aligned ? "Your SNOT build ends exactly at the target position." : result.differenceMm > 0 ? `SNOT Build is ${formatMillimeters(Math.abs(result.differenceMm))} too long.` : `SNOT Build is ${formatMillimeters(Math.abs(result.differenceMm))} too short.`;
    const tileBreakdown = result.snotBuild.addFinishingTile ? `
    <div class="htbi-snot-tile-breakdown">
      <p><strong>SNOT Build:</strong> ${formatMeasurement(snotInput.value, snotInput.unit, { approximate: false })} + 1 finishing Tile</p>
      <p>${formatMillimeters(result.snotBuild.entered.millimeters)} + ${formatMillimeters(result.snotBuild.finishingTileMm)} = ${formatMillimeters(result.snotBuild.final.millimeters)}</p>
      <p>Equivalent thickness: ${plateHeightCount(result.snotBuild.final.plates)} = ${formatMillimeters(result.snotBuild.final.millimeters)}</p>
    </div>` : "";
    return `<article class="htbi-snot-status ${statusClass}">
    <p class="htbi-snot-status-label">${result.aligned ? "Perfect Alignment" : "Offset Detected"}</p>
    <p>${explanation}</p>
    <dl class="htbi-snot-alignment-values">
      <div><dt>Target</dt><dd>${formatMillimeters(result.distanceA.millimeters)}</dd></div>
      <div><dt>SNOT Build</dt><dd>${formatMillimeters(result.distanceB.millimeters)}</dd></div>
      <div><dt>Difference (B \u2212 A)</dt><dd>${formatMillimeters(result.differenceMm, { signed: true })}</dd></div>
    </dl>
    ${tileBreakdown}
    ${result.aligned ? "" : `<p class="htbi-snot-technical-result"><strong>Equivalent signed difference:</strong> ${formatMeasurement(result.difference.studs, "stud", { signed: true })}; ${formatMeasurement(result.difference.plates, "plate", { signed: true })}; ${formatMeasurement(result.difference.bricks, "brick", { signed: true })}</p>`}
  </article>`;
  }
  function renderAlignment(root, instanceId, validatedA, validatedB, finishingTile, { announce, tileToggled }) {
    const target = root.querySelector("[data-alignment-results]");
    const visual = root.querySelector("[data-visual]");
    const status = root.querySelector("[data-alignment-status]");
    if (!validatedA.valid || !validatedB.valid) {
      target.innerHTML = '<p class="htbi-snot-unavailable">Correct both distances to compare their alignment.</p>';
      visual.innerHTML = '<p class="htbi-snot-unavailable">The visual will return when both distances are valid.</p>';
      if (announce) {
        const errors = [];
        if (!validatedA.valid) errors.push(`Target Distance: ${ERROR_MESSAGES[validatedA.code]}`);
        if (!validatedB.valid) errors.push(`SNOT Build: ${ERROR_MESSAGES[validatedB.code]}`);
        status.textContent = errors.join(" ");
      }
      return;
    }
    const snotInput = { value: validatedB.value, unit: validatedB.unit };
    const result = checkSnotAlignment(
      { value: validatedA.value, unit: validatedA.unit },
      snotInput,
      { addFinishingTile: finishingTile }
    );
    target.innerHTML = alignmentResultMarkup(result, snotInput);
    if (announce) {
      const resultSummary = result.aligned ? "Perfect alignment." : `Offset detected. SNOT Build is ${formatMeasurement(Math.abs(result.differenceMm), "millimeter")} too ${result.differenceMm > 0 ? "long" : "short"}.`;
      status.textContent = tileToggled ? `Finishing Tile ${finishingTile ? "added" : "removed"}. SNOT Build total is ${formatMeasurement(result.distanceB.millimeters, "millimeter", { approximate: false })}. ${resultSummary}` : resultSummary;
    }
    const model = createAlignmentDiagramModel({
      distanceAMm: result.distanceA.millimeters,
      distanceBMm: result.distanceB.millimeters,
      differenceMm: result.differenceMm,
      aligned: result.aligned,
      finishingTileMm: result.snotBuild.finishingTileMm
    });
    visual.innerHTML = renderAlignmentSvg(model, instanceId);
  }
  function readField(root, key) {
    return {
      rawValue: root.querySelector(`[data-distance-value="${key}"]`).value,
      unit: root.querySelector(`[data-distance-unit="${key}"]`).value
    };
  }
  function writeState(root, state) {
    for (const key of ["quick", "distanceA", "distanceB"]) {
      root.querySelector(`[data-distance-value="${key}"]`).value = state[key].rawValue;
      root.querySelector(`[data-distance-unit="${key}"]`).value = state[key].unit;
    }
    root.querySelector("[data-preset]").value = state.presetId;
    root.querySelector("[data-finishing-tile]").checked = state.finishingTile;
  }
  function createCalculator(root, instanceId) {
    if (!(root instanceof Element) || root.dataset.htbiSnotMounted === "true") return null;
    root.dataset.htbiSnotMounted = "true";
    root.innerHTML = calculatorMarkup(instanceId);
    let state = createDefaultState();
    let latestWholeAlignment = null;
    function calculate({ announceQuick = false, announceAlignment = false, tileToggled = false } = {}) {
      const quick = validateUiDistance(state.quick.rawValue, state.quick.unit);
      const distanceA = validateUiDistance(state.distanceA.rawValue, state.distanceA.unit);
      const distanceB = validateUiDistance(state.distanceB.rawValue, state.distanceB.unit);
      setValidation(root, "quick", quick);
      setValidation(root, "distanceA", distanceA);
      setValidation(root, "distanceB", distanceB);
      renderQuick(root, quick, announceQuick);
      latestWholeAlignment = renderWholeAlignment(root, state, distanceA, distanceB);
      renderAlignment(root, instanceId, distanceA, distanceB, state.finishingTile, {
        announce: announceAlignment,
        tileToggled
      });
    }
    root.addEventListener("input", (event) => {
      const key = event.target.dataset.distanceValue;
      if (!key) return;
      const current = readField(root, key);
      state = updateFieldState(state, key, current.rawValue, current.unit);
      if (key !== "quick") root.querySelector("[data-preset]").value = "";
      calculate(key === "quick" ? { announceQuick: true } : { announceAlignment: true });
    });
    root.addEventListener("change", (event) => {
      const unitKey = event.target.dataset.distanceUnit;
      if (unitKey) {
        const current = readField(root, unitKey);
        state = updateFieldState(state, unitKey, current.rawValue, current.unit);
        if (unitKey !== "quick") root.querySelector("[data-preset]").value = "";
        calculate(unitKey === "quick" ? { announceQuick: true } : { announceAlignment: true });
        return;
      }
      if (event.target.matches("[data-preset]")) {
        state = applyPresetState(state, event.target.value);
        writeState(root, state);
        calculate({ announceAlignment: true });
        return;
      }
      if (event.target.matches("[data-finishing-tile]")) {
        state = setFinishingTileState(state, event.target.checked);
        root.querySelector("[data-preset]").value = "";
        calculate({ announceAlignment: true, tileToggled: true });
      }
    });
    root.addEventListener("click", (event) => {
      const alignmentButton = event.target.closest("[data-use-alignment]");
      if (alignmentButton) {
        const pairIndex = Number(alignmentButton.dataset.useAlignment);
        const pair = latestWholeAlignment?.recommendedPairs[pairIndex];
        if (!pair) return;
        state = applyWholeAlignmentState(state, pair);
        writeState(root, state);
        calculate({ announceAlignment: true });
        root.querySelector("[data-alignment-status]").textContent = `Whole-number alignment applied. ${root.querySelector("[data-alignment-status]").textContent}`;
        return;
      }
      if (event.target.closest("[data-swap]")) state = swapAlignmentState(state);
      else if (event.target.closest("[data-reset]")) {
        state = createDefaultState();
        writeState(root, state);
        calculate();
        root.querySelector("[data-quick-status]").textContent = "";
        root.querySelector("[data-alignment-status]").textContent = "Calculator reset. Finishing Tile off. Perfect alignment.";
        return;
      } else return;
      writeState(root, state);
      calculate({ announceAlignment: true });
    });
    writeState(root, state);
    calculate();
    return Object.freeze({ root, recalculate: calculate });
  }

  // src/ui/main.js
  var nextInstanceId = 0;
  function createInstanceId() {
    nextInstanceId += 1;
    return `htbi-snot-${nextInstanceId}`;
  }
  function mountSnotOffsetCalculators(scope = document) {
    return [...scope.querySelectorAll("[data-htbi-snot-offset-calculator]")].map((root) => createCalculator(root, createInstanceId())).filter(Boolean);
  }
  function initialize() {
    mountSnotOffsetCalculators();
  }
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initialize, { once: true });
  } else {
    initialize();
  }
})();
